import type { VerifyAppleIAPResponse } from '@/api/pay'
import type { NativeIapRestoreBridge } from '@/utils/platform'

type RestoreDependencies = {
  bridge?: NativeIapRestoreBridge
  syncAppStore?: boolean
  recover: (receiptData: string) => Promise<VerifyAppleIAPResponse>
  onTransactionError?: (error: unknown) => void
}

export type ApplePurchaseRestoreResult = {
  found: number
  restored: number
  failed: number
  unverified: number
}

export async function restoreApplePurchases({
  bridge,
  syncAppStore = false,
  recover,
  onTransactionError,
}: RestoreDependencies): Promise<ApplePurchaseRestoreResult> {
  if (!bridge) throw new Error('Apple restore bridge unavailable')

  const result = await bridge.restorePurchases(syncAppStore)
  const transactionsById = new Map<string, (typeof result.transactions)[number]>()
  for (const transaction of result.transactions) {
    if (!transactionsById.has(transaction.transaction_id)) {
      transactionsById.set(transaction.transaction_id, transaction)
    }
  }
  const transactions = Array.from(transactionsById.values())
  let restored = 0
  let failed = 0

  for (const transaction of transactions) {
    try {
      const confirmation = await recover(transaction.receipt_data)
      if (confirmation.confirmed) restored += 1
    } catch (error) {
      failed += 1
      onTransactionError?.(error)
    }
  }

  return {
    found: transactions.length,
    restored,
    failed,
    unverified: result.unverified_count,
  }
}
